import React, { useState, useEffect, useRef } from 'react';
import { HexalInstance } from '@common/enums';
import { Input } from '@components/ui/input';
import { CURRENT_INSTANCE } from '@lib/utils';

interface IStaticAutocompleteInputProps {
  suggestions?: string[];
  initialValue?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onSelect?: (value: string) => void;
  placeholder?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
}

const StaticAutocompleteInput = ({
  suggestions: staticSuggestions,
  initialValue = '',
  onChange,
  onBlur,
  onSelect,
  placeholder,
  id,
  autoComplete,
  autoFocus = false,
}: IStaticAutocompleteInputProps) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   setInputValue(value);
  //   onChange?.(value);
  //
  //   if (staticSuggestions) {
  //     const matches = staticSuggestions.filter((suggestion) =>
  //       suggestion.toLowerCase().includes(value.toLowerCase())
  //     );
  //     setFilteredSuggestions(matches);
  //     setShowSuggestions(matches.length > 0);
  //   }
  //
  //   setSelectedIndex(-1);
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    onChange?.(value);

    if (staticSuggestions) {
      const searchLower = value.toLowerCase();
      const matches = staticSuggestions
        .filter((suggestion) => {
          const suggestionLower = suggestion.toLowerCase();
          return suggestionLower.includes(searchLower);
        })
        .sort((a, b) => {
          const aLower = a.toLowerCase();
          const bLower = b.toLowerCase();

          // Score system: 2 = starts with search term, 1 = starts with word, 0 = contains
          const getScore = (str: string): number => {
            // Exact match at beginning of string
            if (str.startsWith(searchLower)) return 2;

            // Match at beginning of any word
            const words = str.split(/\s+/);
            for (const word of words) {
              if (word.startsWith(searchLower)) return 1;
            }

            return 0;
          };

          const scoreA = getScore(aLower);
          const scoreB = getScore(bLower);

          // Higher score comes first
          if (scoreA !== scoreB) {
            return scoreB - scoreA;
          }

          // If same score, sort alphabetically
          return aLower.localeCompare(bLower);
        });

      setFilteredSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    }

    setSelectedIndex(-1);
  };
  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    onChange?.(suggestion);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
    onSelect?.(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = prev < filteredSuggestions.length - 1 ? prev + 1 : 0;
        scrollIntoView(newIndex);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : filteredSuggestions.length - 1;
        scrollIntoView(newIndex);
        return newIndex;
      });
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(filteredSuggestions[selectedIndex]);
    }
  };

  const scrollIntoView = (index: number) => {
    const selectedItem = suggestionRefs.current[index];
    if (selectedItem) {
      selectedItem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur]);

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        placeholder={placeholder}
        id={id}
        autoComplete={autoComplete}
        className="!placeholder-white/85 border text-white/90 border-neutral-300 focus:border-primary-500 rounded-lg p-2 w-full bg-transparent"
      />
      {showSuggestions && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full bg-xircle-main rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto border border-neutral-300"
        >
          {filteredSuggestions.slice(0, 7).map((suggestion, index) => (
            <div
              key={index}
              ref={(el) => {
                suggestionRefs.current[index] = el;
              }}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`px-4 py-2 cursor-pointer border-b border-neutral-200 hover:bg-xircle-main
            ${
              index === selectedIndex
                ? 'bg-xircle-main border-l-4 border-l-primary-500 shadow-lg'
                : 'bg-xircle-secondary'
            }
            ${CURRENT_INSTANCE.name === HexalInstance.Default ? 'text-hexal-dark-text-color hover:text-slate-200' : ''}`}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaticAutocompleteInput;
